// Chip-based editor for the friendly rule syntax (see OperaRuleExpression).
//
// The condition is a sequence of atomic chips inside a contenteditable line:
//   [BA:PIANO BAR ▾] [AND] [(] [TAG:BEBIDA ▾] [OR] [TAG:COMIDA ▾] [)]
// Typing # (or #BA…) spawns a highlighted pending chip with a dropdown + search box —
// entities are picked, never typed, so there is nothing to mistype. Clicking a chip
// reopens the picker to change it. AND / OR / && / || become operator chips, ( ) become
// paren chips, each with their own color. The serialized token string is handed to the
// owner on every change; incomplete content (pending chip, stray text) reports null.
class OperaRuleTokenEditor {

    private container: HTMLElement = null;
    private ctx: RuleTokenContext = null;
    private onChange: (friendly: string) => void = null;

    private dropdown: HTMLElement = null;
    private searchInput: HTMLInputElement = null;
    private itemsLayer: HTMLElement = null;
    private pendingChip: HTMLElement = null;    // chip being built or re-picked
    private pendingStage: string = null;        // "kind" | a kind (picking the name)

    init(container: HTMLElement, ctx: RuleTokenContext, onChange: (friendly: string) => void) {
        this.container = container;
        this.ctx = ctx;
        this.onChange = onChange;

        container.classList.add("rule-token-editor");
        container.setAttribute("contenteditable", "true");
        container.setAttribute("spellcheck", "false");

        container.addEventListener("keydown", (ev: KeyboardEvent) => this.editorKeyDown(ev));
        container.addEventListener("input", () => this.editorInput());
        container.addEventListener("click", (ev: MouseEvent) => {
            let chip = (ev.target as HTMLElement).closest ? (ev.target as HTMLElement).closest(".tok-entity") as HTMLElement : null;
            if (chip != null && this.container.contains(chip)) this.repickChip(chip);
        });
        container.addEventListener("blur", () => setTimeout(() => {
            if (this.dropdown != null && this.dropdown.contains(document.activeElement)) return;
            this.cancelPending();
        }, 200));
    }

    // MARK: - Content

    setTokens(friendly: string) {
        this.closeDropdown();
        this.container.innerHTML = "";
        let text = (friendly || "").trim();
        if (text.length == 0) return;

        // The friendly grammar tokenized back into chips.
        let re = /#(BA|DESK|PM|TAG|CARD|MENU|PRODUCT):([^#()]*?)(?=\s+(?:AND|OR)\b|\s*[()]|\s*$)|#(MENU)(?=\s|$|[()])|\b(AND|OR)\b|([()])/gi;
        let m: RegExpExecArray;
        while ((m = re.exec(text)) != null) {
            if (m[1] != null) this.container.appendChild(this.entityChip(m[1].toUpperCase(), m[2].trim()));
            else if (m[3] != null) this.container.appendChild(this.entityChip("MENU", ""));
            else if (m[4] != null) this.container.appendChild(this.opChip(m[4].toUpperCase()));
            else if (m[5] != null) this.container.appendChild(this.parenChip(m[5]));
        }
    }

    // null while incomplete (pending chip open, stray text in the line).
    serialize(): string {
        let parts: string[] = [];
        for (let i = 0; i < this.container.childNodes.length; i++) {
            let node = this.container.childNodes[i];
            if (node.nodeType == Node.TEXT_NODE) {
                if ((node.textContent || "").trim().length > 0) return null;
                continue;
            }
            let el = node as HTMLElement;
            if (el.classList == null) continue;
            if (el.classList.contains("tok-pending")) return null;
            if (el.classList.contains("tok-entity")) {
                let name = el.getAttribute("data-name") || "";
                parts.push(name.length > 0 ? "#" + el.getAttribute("data-kind") + ":" + name : "#" + el.getAttribute("data-kind"));
            }
            else if (el.classList.contains("tok-op")) parts.push(el.getAttribute("data-op"));
            else if (el.classList.contains("tok-paren")) parts.push(el.getAttribute("data-paren"));
        }
        return parts.join(" ");
    }

    private changed() {
        if (this.onChange != null) this.onChange(this.serialize());
    }

    // MARK: - Chips

    private entityChip(kind: string, name: string): HTMLElement {
        let chip = document.createElement("span");
        chip.classList.add("tok", "tok-entity");
        chip.setAttribute("contenteditable", "false");
        chip.setAttribute("data-kind", kind);
        chip.setAttribute("data-name", name);
        chip.innerHTML = "<span class='k'></span><span class='n'></span><span class='arrow'>▾</span>";
        (chip.querySelector(".k") as HTMLElement).textContent = name.length > 0 ? kind + ":" : "";
        (chip.querySelector(".n") as HTMLElement).textContent = name.length > 0 ? name : kind;
        chip.title = name.length > 0 ? OperaRuleExpression.kindLabel(kind)
                   : MIOLocalizeString("ANY", "any") + " " + OperaRuleExpression.kindLabel(kind);
        return chip;
    }

    private opChip(op: string): HTMLElement {
        let chip = document.createElement("span");
        chip.classList.add("tok", "tok-op");
        chip.setAttribute("contenteditable", "false");
        chip.setAttribute("data-op", op);
        chip.textContent = op;
        return chip;
    }

    private parenChip(paren: string): HTMLElement {
        let chip = document.createElement("span");
        chip.classList.add("tok", "tok-paren");
        chip.setAttribute("contenteditable", "false");
        chip.setAttribute("data-paren", paren);
        chip.textContent = paren;
        return chip;
    }

    private pendingChipElement(label: string): HTMLElement {
        let chip = document.createElement("span");
        chip.classList.add("tok", "tok-pending");
        chip.setAttribute("contenteditable", "false");
        chip.textContent = label;
        return chip;
    }

    // MARK: - Typing

    private editorKeyDown(ev: KeyboardEvent) {
        if (ev.key == "#") {
            ev.preventDefault();
            this.startPending();
            return;
        }
        if (ev.key == "(" || ev.key == ")") {
            ev.preventDefault();
            this.insertAtCaret(this.parenChip(ev.key));
            this.changed();
            return;
        }
        if (ev.key == "Backspace" || ev.key == "Delete") {
            let removed = this.removeChipAtCaret(ev.key == "Backspace");
            if (removed) { ev.preventDefault(); this.changed(); }
            return;
        }
        if (ev.key == "Enter") ev.preventDefault();
    }

    private editorInput() {
        // Convert operator words typed as free text: AND / OR / && / || + a space.
        for (let i = 0; i < this.container.childNodes.length; i++) {
            let node = this.container.childNodes[i];
            if (node.nodeType != Node.TEXT_NODE) continue;
            let text = node.textContent || "";
            let m = text.match(/(?:\b(AND|OR)\b|(&&)|(\|\|))\s/i);
            if (m == null) continue;

            let op = m[1] != null ? m[1].toUpperCase() : (m[2] != null ? "AND" : "OR");
            let before = text.substring(0, m.index);
            let after = text.substring(m.index + m[0].length);
            let chip = this.opChip(op);

            let parent = node.parentNode;
            if (before.trim().length > 0) parent.insertBefore(document.createTextNode(before), node);
            parent.insertBefore(chip, node);
            node.textContent = after;
            this.placeCaretAfter(after.length > 0 ? node : chip);
        }
        this.changed();
    }

    private insertAtCaret(chip: HTMLElement) {
        let sel = window.getSelection();
        if (sel != null && sel.rangeCount > 0 && this.container.contains(sel.anchorNode)) {
            let range = sel.getRangeAt(0);
            range.deleteContents();
            range.insertNode(chip);
        }
        else {
            this.container.appendChild(chip);
        }
        this.placeCaretAfter(chip);
    }

    private placeCaretAfter(node: Node) {
        let sel = window.getSelection();
        if (sel == null) return;
        let range = document.createRange();
        range.setStartAfter(node);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    }

    private removeChipAtCaret(backwards: boolean): boolean {
        let sel = window.getSelection();
        if (sel == null || sel.rangeCount == 0 || sel.isCollapsed == false) return false;
        let node = sel.anchorNode;
        let offset = sel.anchorOffset;
        if (this.container.contains(node) == false) return false;

        let target: Node = null;
        if (node.nodeType == Node.TEXT_NODE) {
            let text = node.textContent || "";
            if (backwards && offset == 0) target = node.previousSibling;
            else if (!backwards && offset == text.length) target = node.nextSibling;
        }
        else if (node == this.container) {
            target = backwards ? this.container.childNodes[offset - 1] : this.container.childNodes[offset];
        }

        if (target == null || target.nodeType != Node.ELEMENT_NODE) return false;
        if ((target as HTMLElement).classList.contains("tok") == false) return false;
        this.container.removeChild(target);
        return true;
    }

    // MARK: - Picker dropdown

    private startPending() {
        this.cancelPending();
        this.pendingChip = this.pendingChipElement("#…");
        this.insertAtCaret(this.pendingChip);
        this.pendingStage = "kind";
        this.openDropdown();
        this.changed();
    }

    private repickChip(chip: HTMLElement) {
        this.cancelPending();
        this.pendingChip = chip;
        this.pendingStage = chip.getAttribute("data-kind");
        this.openDropdown();
    }

    private openDropdown() {
        this.closeDropdown();

        this.dropdown = document.createElement("div");
        this.dropdown.classList.add("rule-suggest");

        this.searchInput = document.createElement("input");
        this.searchInput.type = "text";
        this.searchInput.classList.add("rule-suggest-search");
        this.searchInput.placeholder = MIOLocalizeString("TYPE_TO_FILTER", "type to filter…");
        this.dropdown.appendChild(this.searchInput);

        this.itemsLayer = document.createElement("div");
        this.dropdown.appendChild(this.itemsLayer);

        // On the document, not inside the row: the table rows clip what overflows them,
        // so a dropdown hosted in the row opened below its edge and could not be seen
        // or clicked. Positioned from the chip's place on screen.
        document.body.appendChild(this.dropdown);
        let rect = this.pendingChip.getBoundingClientRect();
        let left = Math.min(rect.left, Math.max(0, window.innerWidth - 300));
        let below = rect.bottom + 2;
        let fits = below + 240 <= window.innerHeight;
        this.dropdown.style.position = "fixed";
        this.dropdown.style.left = left + "px";
        if (fits) this.dropdown.style.top = below + "px";
        else this.dropdown.style.bottom = (window.innerHeight - rect.top + 2) + "px";

        this.searchInput.addEventListener("input", () => this.searchChanged());
        this.searchInput.addEventListener("keydown", (ev: KeyboardEvent) => {
            if (ev.key == "Escape") { ev.stopPropagation(); this.cancelPending(); this.container.focus(); }
            else if (ev.key == "Enter" || ev.key == "Tab") {
                ev.preventDefault(); ev.stopPropagation();
                this.pickFirst();
            }
        });
        this.searchInput.addEventListener("blur", () => setTimeout(() => {
            if (this.dropdown != null && this.dropdown.contains(document.activeElement)) return;
            this.cancelPending();
        }, 200));

        this.renderItems();
        this.searchInput.focus();
    }

    // The search box follows the way people actually type a condition: "TAG:SERVICE
    // CHARGE" in one go (the kind is taken as soon as the colon arrives), and
    // "… AND" / "… OR" to take the match and go on with the next token.
    private searchChanged() {
        if (this.searchInput == null) return;
        let value = this.searchInput.value || "";

        if (this.pendingStage == "kind") {
            let m = value.match(/^\s*#?(BA|DESK|PM|TAG|CARD|MENU|PRODUCT)\s*:(.*)$/i);
            if (m != null) {
                this.pickKind(m[1].toUpperCase());
                this.searchInput.value = m[2].replace(/^\s+/, "");
                this.renderItems();
                return;
            }
        }
        else {
            let m = value.match(/^(.*\S)\s+(AND|OR|&&|\|\|)\s$/i);
            if (m != null) {
                this.searchInput.value = m[1];
                this.renderItems();
                let op = m[2].toUpperCase() == "OR" || m[2] == "||" ? "OR" : "AND";
                if (this.pickFirst()) {
                    this.insertAtCaret(this.opChip(op));
                    this.changed();
                }
                return;
            }
        }
        this.renderItems();
    }

    // Takes the first suggestion. True when something was picked.
    private pickFirst(): boolean {
        if (this.itemsLayer == null) return false;
        let first = this.itemsLayer.querySelector(".rule-suggest-item") as HTMLElement;
        if (first == null) return false;
        first.dispatchEvent(new Event("mousedown"));
        return true;
    }

    private renderItems() {
        if (this.itemsLayer == null) return;
        let typed = (this.searchInput.value || "").trim();
        let filter = typed.toUpperCase();
        this.itemsLayer.innerHTML = "";

        let items: {display: string, hint: string, pick: () => void}[] = [];

        if (this.pendingStage == "kind") {
            for (let kind of ["BA", "DESK", "PM", "TAG", "CARD", "MENU", "PRODUCT"]) {
                let label = OperaRuleExpression.kindLabel(kind);
                if (filter.length > 0 && kind.indexOf(filter) != 0 && label.toUpperCase().indexOf(filter) < 0) continue;
                items.push({display: "#" + kind + ":", hint: label, pick: () => this.pickKind(kind)});
            }
        }
        else {
            let kind = this.pendingStage;
            if (kind == "MENU" && filter.length == 0) {
                items.push({display: MIOLocalizeString("ANY_MENU", "any menu"), hint: MIOLocalizeString("IS_A_MENU", "is a menu"), pick: () => this.pickName("MENU", "")});
            }
            let pool = kind == "TAG" ? this.ctx.tags : kind == "CARD" ? this.ctx.subtypes : (this.ctx.names[kind] || []);
            let exact = false;
            for (let name of pool) {
                if (filter.length > 0 && name.toUpperCase().indexOf(filter) < 0) continue;
                if (name.toUpperCase() == filter) exact = true;
                items.push({display: name, hint: OperaRuleExpression.kindLabel(kind), pick: () => this.pickName(kind, name)});
                if (items.length >= 10) break;
            }
            // A tag or card subtype is a plain string in the predicate (tags CONTAINS
            // '…'): one that is not in the suggestions yet — a tag just created on the
            // products — can still be typed. Entities (areas, desks, pay methods,
            // menus, products) are picked by name only: their predicate needs the id.
            if ((kind == "TAG" || kind == "CARD") && typed.length > 0 && exact == false) {
                let name = kind == "CARD" ? typed.toLowerCase() : typed;
                let item = {display: name, hint: MIOLocalizeString("USE_AS_TYPED", "use as typed"), pick: () => this.pickName(kind, name)};
                if (items.length == 0) items.push(item); else items.splice(Math.min(items.length, 9), 0, item);
            }
        }

        for (let item of items) {
            let row = document.createElement("div");
            row.classList.add("rule-suggest-item");
            let name = document.createElement("span");
            name.textContent = item.display;
            let hint = document.createElement("span");
            hint.classList.add("hint");
            hint.textContent = item.hint;
            row.appendChild(name);
            row.appendChild(hint);
            row.addEventListener("mousedown", (ev) => { ev.preventDefault(); item.pick(); });
            this.itemsLayer.appendChild(row);
        }
    }

    private pickKind(kind: string) {
        this.pendingStage = kind;
        this.pendingChip.textContent = "#" + kind + ":…";
        this.searchInput.value = "";
        this.renderItems();
        this.searchInput.focus();
    }

    private pickName(kind: string, name: string) {
        let chip = this.entityChip(kind, name);
        this.container.replaceChild(chip, this.pendingChip);
        this.pendingChip = null;
        this.pendingStage = null;
        this.closeDropdown();
        this.container.focus();
        this.placeCaretAfter(chip);
        this.changed();
    }

    private cancelPending() {
        if (this.pendingChip != null && this.pendingChip.classList.contains("tok-pending")) {
            if (this.pendingChip.parentNode != null) this.pendingChip.parentNode.removeChild(this.pendingChip);
        }
        this.pendingChip = null;
        this.pendingStage = null;
        this.closeDropdown();
        this.changed();
    }

    private closeDropdown() {
        if (this.dropdown != null && this.dropdown.parentNode != null) this.dropdown.parentNode.removeChild(this.dropdown);
        this.dropdown = null;
        this.searchInput = null;
        this.itemsLayer = null;
    }
}
